import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, FileDown } from "lucide-react";
import { company, products } from "../data/content";
import { useCmsList, useCmsSingleton } from "../hooks/useCmsContent";
import BrandLogo from "./BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOOTER_LINKS_FALLBACK = [["About Us", "/about"], ["Engineering & Manufacturing", "/capabilities"], ["Facilities — Tarapur", "/facilities"], ["Projects", "/projects"], ["Quality & Testing", "/quality"], ["Certifications & Approvals", "/certifications"], ["Careers", "/careers"], ["Contact", "/contact"]];

const Footer = () => {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    fetch(`${API}/company-profile`).then((r) => (r.ok ? r.json() : {})).then((d) => setProfile(d && d.doc_url ? d : null)).catch(() => {});
  }, []);
  const cmsNav = useCmsList("nav_item", []);
  const cmsContact = useCmsSingleton("contact", {});
  const navMeta = useCmsSingleton("navigation_meta", {});
  const cmsProducts = useCmsList("product", products);
  const footerLinks = cmsNav.filter((i) => i.area === "footer").map((i) => [i.label, i.to]);
  const links = footerLinks.length ? footerLinks : FOOTER_LINKS_FALLBACK;
  const address = (cmsContact.address_office || []).length ? cmsContact.address_office : company.address;
  const phone = cmsContact.phone || company.phone;
  const email = cmsContact.email || company.email;
  const domain = cmsContact.domain || company.domain;
  const tagline = navMeta.footer_tagline || "An established engineering and fabrication organisation delivering world-class products and services — quality workmanship, competitive costs and on-time delivery — for the hydrocarbon and process industries.";
  const note = navMeta.footer_note || "MUMBAI · TARAPUR · grengg.com";
  return (
  <footer className="bg-[#0A1128] text-slate-400" data-testid="site-footer">
    <div className="container-gre pt-16 pb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
      <div>
        <div className="flex items-center gap-3">
          <BrandLogo />
          <span className="leading-none">
            <span className="font-display font-extrabold tracking-tight text-white block">G. R. ENGINEERING</span>
            <span className="font-mono-tech text-[9px] tracking-[0.3em] text-slate-500 uppercase">Private Limited · Est. 1966</span>
          </span>
        </div>
        <p className="mt-6 text-sm leading-relaxed">{tagline}</p>
        <p className="mt-4 font-mono-tech text-[11px] uppercase tracking-widest text-slate-500">ASME 'U' &amp; 'U2' Stamp Holder</p>
      </div>
      <div>
        <h4 className="font-display text-white font-bold text-sm uppercase tracking-widest">Company</h4>
        <ul className="mt-5 space-y-2.5 text-sm">
          {links.map(([l, to]) => (
            <li key={l}><Link to={to} className="hover:text-[#C5221F] transition-colors duration-200">{l}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-display text-white font-bold text-sm uppercase tracking-widest">Products</h4>
        <ul className="mt-5 space-y-2.5 text-sm">
          {cmsProducts.map((p) => (
            <li key={p.id}><Link to={`/products#${p.id}`} className="hover:text-[#C5221F] transition-colors duration-200">{p.name}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-display text-white font-bold text-sm uppercase tracking-widest">Head Office</h4>
        <ul className="mt-5 space-y-3.5 text-sm">
          <li className="flex gap-2.5"><MapPin size={15} className="text-[#C5221F] shrink-0 mt-0.5" /><span>{address.join(", ")}</span></li>
          <li><a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="flex gap-2.5 hover:text-white transition-colors duration-200" data-testid="footer-phone"><Phone size={15} className="text-[#C5221F] shrink-0 mt-0.5" />{phone}</a></li>
          <li><a href={`mailto:${email}`} className="flex gap-2.5 hover:text-white transition-colors duration-200" data-testid="footer-email"><Mail size={15} className="text-[#C5221F] shrink-0 mt-0.5" />{email}</a></li>
          <li className="flex gap-2.5"><Globe size={15} className="text-[#C5221F] shrink-0 mt-0.5" />{domain}</li>
          {profile && (
            <li><a href={profile.doc_url} target="_blank" rel="noopener noreferrer" className="flex gap-2.5 hover:text-white transition-colors duration-200" data-testid="footer-profile-link"><FileDown size={15} className="text-[#C5221F] shrink-0 mt-0.5" />{profile.title}{profile.version ? ` (${profile.version})` : ""}</a></li>
          )}
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="container-gre py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <p>© {new Date().getFullYear()} G. R. Engineering Private Limited. All rights reserved.</p>
        <p className="font-mono-tech tracking-wider">{note}</p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;

